import Link from 'next/link';

import { formatPhoneNumber } from './helpers';
import { FooterMenuFragmentFragment } from '../../__generated__/graphql';
import Divider from '../../components/Divider';
import Svg from '../../components/Svg';
import { Heading5, Paragraph, TextLink } from '../../components/Text';
import { sanitizeHtml } from '../../utils/sanitzeHtml';
import { flatListToHierarchical } from '../helpers';
import Section from '../Section';

import style from './footer.module.css';

interface FooterProps {
  menu: FooterMenuFragmentFragment;
}

const Footer = ({
  menu,
}: FooterProps) => {
  const megaMenuItems = flatListToHierarchical(menu.menuItems?.nodes ?? []);

  const blockFooter = menu.blockFooter;

  const address = blockFooter?.footerAddress;
  const phoneNumber = blockFooter?.phone?.toString();
  const footerDisclosure = blockFooter?.footerDisclosure;
  const footerLogo = blockFooter?.footerLogo;
  const socialIcons = blockFooter?.socialMediaIcons;

  const addressLines = address?.split('<br />\r\n');

  return (
    <footer className={style.footer}>
      <Section
        backgroundColor="black"
        id={null}
      >
        <div className={style.logoArea}>
          {footerLogo && (
            <Svg image={footerLogo} />
          )}

          {!!socialIcons?.length && (
            <ul className={style.socialLinks}>
              {socialIcons.map(link => (
                <li key={link?.socialMediaUrl ?? ''}>
                  <Link
                    aria-label={link?.iconImage?.node.altText ?? ''}
                    href={link?.socialMediaUrl ?? ''}
                    rel="noreferrer"
                    target="_blank"
                  >
                    <Svg image={link?.iconImage} />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div
          className={style.linkGrid}
        >
          <div>
            <address>
              {addressLines?.map(line => (
                <Paragraph key={line}>
                  {line}
                </Paragraph>
              ))}

              {phoneNumber && (
                <Paragraph>
                  <TextLink
                    linkFragment={{
                      title: formatPhoneNumber(phoneNumber),
                      url: `tel:${phoneNumber}`,
                    }}
                  />
                </Paragraph>
              )}
            </address>
          </div>

          {megaMenuItems.map(linkSection => {
            return (
              <div key={linkSection.id}>
                <Heading5
                  className={style.linkSectionListTitle}
                  fontColor="turquoise"
                  uppercase
                >
                  {linkSection.label}
                </Heading5>

                <ul className={style.linkSectionList}>
                  {linkSection.children.map(link => (
                    <li key={link.id}>
                      <TextLink
                        linkFragment={{
                          title: link.label,
                          url: link.uri ?? '',
                        }}
                      />
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </Section>

      <Divider />

      <Section
        backgroundColor="black"
        id={null}
      >
        <div
          className={style.legalContent}
          // eslint-disable-next-line @typescript-eslint/naming-convention
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(footerDisclosure ?? '') }}
        />
      </Section>
    </footer>
  );
};

export default Footer;
